import moment from 'moment';
import { call, put, select, takeEvery } from 'redux-saga/effects';
import {
    appInitAction,
    loadAccountHistoryFailure,
    loadAccountHistoryInfoFailure,
    loadAccountHistoryInfoSuccess,
    loadAccountHistorySuccess,
} from '../../app/appActions';
import { LOAD_ACCOUNT_HISTORY_INFO_START, LOAD_ACCOUNT_HISTORY_START } from '../../app/appConstants';
import { getUserEmailSelector, getUserSelectedAccountCode } from '../../app/appSelectors';
import { failurePut, IFailurePutCall } from '../../app/failurePut';
import { getRequest, postRequest } from '../../app/request';
import {
    accountGetPortfolioFailure,
    accountGetPortfolioSuccess,
    accountGetTradingTokenFailure,
    accountGetTradingTokenSuccess,
    payoutGetTransactionsFailure,
    payoutGetTransactionsSuccess,
    refillDemoFailure,
    refillDemoSuccess,
    resendUserConfirmEmailFailure,
    resendUserConfirmEmailSuccess,
    setConfirmEmailRequestedTimeAction,
} from './accountActions';
import {
    ACCOUNT_GET_PORTFOLIO_START,
    ACCOUNT_GET_TRADING_TOKEN_START,
    PAYOUT_TRANSACTIONS_START,
    REFILL_DEMO_START,
    RESEND_USER_CONFIRM_EMAIL_START,
} from './accountConstants';
import {
    bonusesSetActiveListSuccess,
    bonusesSetAvailableList,
    setActivePromocodesSuccess,
    setUsedPromocodesAction,
} from '../Bonus/bonusesActions';
import {
    IAccountPortfolio,
    IHistoryResponse,
    ILoadAccountHistoryStartPayload,
    IPayloadTransaction,
    ITradingToken
} from './types/accountActions.t';
import appHistory from '../../app/appHistory';

export function* accountGetPortfolioSaga() {
    try {
        const data: IAccountPortfolio = yield call(getRequest, '/portfolio');

        yield put(accountGetPortfolioSuccess(data));
        yield put(bonusesSetActiveListSuccess(data?.bonusesActive));
        yield put(bonusesSetAvailableList(data?.bonusesAvailable));
        yield put(setActivePromocodesSuccess(data?.promocodesActive));
        yield put(setUsedPromocodesAction(data?.promocodesUsed));
    } catch (e: any) {
        yield call(failurePut, e, accountGetPortfolioFailure);
    }
}

export function* accountGetTradingTokenSaga() {
    try {
        const data: ITradingToken = yield call(getRequest, '/tradingToken');
        yield put(accountGetTradingTokenSuccess(data));
    } catch (e: any) {
        yield call<IFailurePutCall>(failurePut, e, accountGetTradingTokenFailure);
    }
}

export function* payoutGetTransactionsSaga() {
    try {
        const data: IPayloadTransaction = yield call(getRequest, '/transactions');
        yield put(payoutGetTransactionsSuccess(data));
    } catch (e: any) {
        yield call(failurePut, e, payoutGetTransactionsFailure);
    }
}

export function* resendUserConfirmEmailSaga() {
    try {
        const email: string = yield select(getUserEmailSelector);
        yield call(postRequest, '/user/resend', { email, requestType: 'confirm' });
        yield put(resendUserConfirmEmailSuccess());
    } catch (e: any) {
        if (e.response?.data?.messageCode === 'error.email_already_confirmed') {
            yield put(appInitAction());
        }
        yield call(failurePut, e, resendUserConfirmEmailFailure);

    } finally {
        const currentTime = moment().toISOString();
        yield put(setConfirmEmailRequestedTimeAction(currentTime));
    }
}

export function* loadAccountHistorySaga({ payload }: { payload: ILoadAccountHistoryStartPayload }) {
    try {
        const accountCode = encodeURIComponent(payload.accountCode);
        const url = `/account/${accountCode}/history`;
        const searchParams = new URLSearchParams();
        if (payload.type) {
            url.concat(`/${payload.type}`);
        }
        if (payload.symbol) {
            searchParams.append('symbol', payload.symbol);
        }
        const data: IHistoryResponse = yield call(getRequest, `${url}?${searchParams.toString()}`);
        yield put(loadAccountHistorySuccess(data));
    } catch (e: any) {
        yield call(failurePut, e, loadAccountHistoryFailure);
    }
}

interface ILoadAccountHistoryInfoSaga {
    payload: {
        id: string;
        accountCode: string;
        type: 'position' | 'transaction';
    };
}

export function* loadAccountHistoryInfoSaga({ payload }: ILoadAccountHistoryInfoSaga) {
    try {
        const accountCode = encodeURIComponent(payload.accountCode);
        const data: {} = yield call(getRequest, `/account/${accountCode}/history/${payload.type}/${payload.id}`);
        yield put(loadAccountHistoryInfoSuccess({ type: payload.type, info: data || {} }));
    } catch (e: any) {
        yield call(failurePut, e, loadAccountHistoryInfoFailure);
    }
}

export function* refillDemoSaga() {
    try {
        const accountCode: string = yield select(getUserSelectedAccountCode);
        const encodedAccountCode: string = encodeURIComponent(accountCode);
        yield call(postRequest, `/account/${encodedAccountCode}/refill`);
        yield put(refillDemoSuccess());
    } catch (e: any) {
        yield call(failurePut, e, refillDemoFailure);
    }
    appHistory.goBack();
}

export function* accountSagas() {
    yield takeEvery(ACCOUNT_GET_PORTFOLIO_START, accountGetPortfolioSaga);
    yield takeEvery(ACCOUNT_GET_TRADING_TOKEN_START, accountGetTradingTokenSaga);
    yield takeEvery(PAYOUT_TRANSACTIONS_START, payoutGetTransactionsSaga);
    yield takeEvery(RESEND_USER_CONFIRM_EMAIL_START, resendUserConfirmEmailSaga);
    //@ts-ignore
    yield takeEvery(LOAD_ACCOUNT_HISTORY_START, loadAccountHistorySaga);
    //@ts-ignore
    yield takeEvery(LOAD_ACCOUNT_HISTORY_INFO_START, loadAccountHistoryInfoSaga);
    yield takeEvery(REFILL_DEMO_START, refillDemoSaga);
}
